// CR151: Deal Policy page - Operations > Deal Policy (Rules)
import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Save, RotateCcw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { useDealPolicy } from "@/hooks/useDealPolicy";
import { useAuth } from "@/contexts/AuthContext";
import { EligibilityRulesEditor } from "@/components/admin/deal-policy/EligibilityRulesEditor";
import { IssueSeverityEditor } from "@/components/admin/deal-policy/IssueSeverityEditor";
import { SimulatePanel } from "@/components/admin/deal-policy/SimulatePanel";
import type { DealPolicyConfig } from "@/lib/deal-policy/types";

export default function DealPolicy() {
  const { activeConfig, isLoading, isSaving, saveConfig, refetch } = useDealPolicy();
  const { user } = useAuth();
  const [draftConfig, setDraftConfig] = useState<DealPolicyConfig | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize draft from active config
  useEffect(() => {
    if (activeConfig?.config_json && !draftConfig) {
      setDraftConfig(activeConfig.config_json);
    }
  }, [activeConfig]);

  // Track changes
  useEffect(() => {
    if (draftConfig && activeConfig?.config_json) {
      const changed = JSON.stringify(draftConfig) !== JSON.stringify(activeConfig.config_json);
      setHasChanges(changed);
    }
  }, [draftConfig, activeConfig]);

  const handleSave = () => {
    if (!activeConfig?.id || !draftConfig || !user?.email) return;
    saveConfig({
      id: activeConfig.id,
      config_json: draftConfig,
      updated_by: user.email,
    });
    setHasChanges(false);
  };

  const handleDiscard = () => {
    if (activeConfig?.config_json) {
      setDraftConfig({ ...activeConfig.config_json });
      setHasChanges(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </AdminLayout>
    );
  }

  if (!activeConfig || !draftConfig) {
    return (
      <AdminLayout>
        <div className="p-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                No Active Policy
              </CardTitle>
              <CardDescription>
                No deal policy configuration found. Please seed the default configuration.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-background sticky top-0 z-10">
          <div>
            <h1 className="text-xl font-semibold">Deal Policy (Rules)</h1>
            <p className="text-sm text-muted-foreground">
              Configure eligibility scope rules, classification hints, and issue severity.
              Applied during Normalize (S4).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={hasChanges ? "default" : "secondary"}>
              {hasChanges ? "Unsaved changes" : "Saved"}
            </Badge>
            <Badge variant="outline">
              {activeConfig.name} ({activeConfig.version})
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDiscard}
              disabled={!hasChanges || isSaving}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Discard
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              <Save className="h-4 w-4 mr-1" />
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1 p-4">
          <div className="max-w-5xl mx-auto space-y-4">
            <Accordion type="multiple" defaultValue={["eligibility", "severity", "simulate"]} className="space-y-4">
              {/* Eligibility Scope Rules */}
              <AccordionItem value="eligibility" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">Eligibility Scope Rules</span>
                    <Badge variant="outline">
                      {draftConfig.eligibility_scope_rules.filter(r => r.enabled).length} active
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <EligibilityRulesEditor
                    rules={draftConfig.eligibility_scope_rules}
                    onChange={(rules) => setDraftConfig({ ...draftConfig, eligibility_scope_rules: rules })}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* Issue Severity Matrix */}
              <AccordionItem value="severity" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">Issue Severity Matrix</span>
                    <Badge variant="outline">
                      {Object.keys(draftConfig.issue_severity_map).length} issues
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <IssueSeverityEditor
                    severityMap={draftConfig.issue_severity_map}
                    onChange={(map) => setDraftConfig({ ...draftConfig, issue_severity_map: map })}
                  />
                </AccordionContent>
              </AccordionItem>

              {/* Simulate Panel */}
              <AccordionItem value="simulate" className="border rounded-lg px-4">
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">Simulate / Test</span>
                    <Badge variant="secondary">Live preview</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-4">
                  <SimulatePanel config={draftConfig} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </ScrollArea>
      </div>
    </AdminLayout>
  );
}
